import { isEqual } from "lodash";
import React, { FC, JSX, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProjectionProviderType } from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { DeleteProjectionProvider, ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { convertServicingProvidersToAccessPoints } from "@app/projects/projection-provider/utils";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { NextClosestImpactProvider } from "@app/projects/results/servicing-provider/nextClosestImpactProvider";
import {
  CareJourneyScoresValueEnum,
  EffectivenessEfficiencyValueEnum,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum,
  TopCondition,
  TopProcedure
} from "@app/projects/servicing-provider";
import { NextClosestImpactProviderTable } from "@app/projects/servicing-provider/NextClosestImpactProviderTable";
import NextClosestProviderService from "@app/projects/servicing-provider/nextClosestProviderService";
import { buildCommonImpactProviderColumns, getCargoDataForProvider, translateNcpFilter } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { GainsPercentEnum, ImpactProviderTableData, NcpEnum, NextClosestImpactProviderTableDataWithCargo, NPF } from "@app/projects/servicing-provider/types";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { Flyout, FlyoutContext, FlyoutLink } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import Project from "@app/projects/project";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import ConfidenceTierSearchFilters from "@app/projects/servicing-provider/provider-claims/ConfidenceTierSearchFilters";
import ProviderClaimsFilters from "@app/projects/servicing-provider/provider-claims/ProviderClaimsFilters";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { SearchBox } from "@app/shared/SearchBox";
import { Toggle } from "@app/shared/Toggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ViewableField, TableType } from "@app/viewable-fields/viewableFields";


type DisplayedProviderType = "excluded" | "nonExcluded";

interface NextClosestImpactProvidersPanelProps {
  editRestricted: boolean;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  processProviderChanges: (doItNow: boolean) => void;
  project: Project;
  providerType: ProviderNetworkTypeEnum;
  readOnly: boolean;
  regionSetId: number;
  runConfiguration: ProjectRunConfigurationResult;
  setProjectHasUnprocessedProjectionProviders: (doesIt: boolean) => void;
  setShouldRefreshMarketProviders: (doesIt: boolean) => void;
  setShouldRefreshProspectProviders: (doesIt: boolean) => void;
  shouldRefreshMarketProviders: boolean;
  shouldRefreshProspectProviders: boolean;
  specialtyGroupCode: string;
  viewableFields: ViewableField[];
  showProviderValidations?: boolean;
  showSpecialtyPredictionModel?: boolean;
  showPgrConfidenceTier?: boolean;
}

const NextClosestImpactProvidersPanel: FC<React.PropsWithChildren<NextClosestImpactProvidersPanelProps>> = (props: NextClosestImpactProvidersPanelProps) => {
  const [accessStandard, setAccessStandard] = useState("");
  const [hasExcludedRecords, setHasExcludedRecords] = useState(false);
  const [displayedProviderType, setDisplayedProviderType] = useState<DisplayedProviderType>("nonExcluded");
  const [displayedProviderTypeChanged, setDisplayedProviderTypeChanged] = useState<boolean>(false);
  const [providerData, setProviderData] = useState<NextClosestImpactProviderTableDataWithCargo[]>(undefined);
  const [ncpFilter, setNcpFilter] = useState<NcpEnum>(NcpEnum.All);
  const [selectedColumn, setSelectedColumn] = useState<string>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirections>(undefined);
  const [selectedCargoColumn, setSelectedCargoColumn] = useState<string>(undefined);
  const [searchValue, setSearchValue] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [showTime, setShowTime] = useState(false);
  const [showDist, setShowDist] = useState(false);
  const providerIdToProjectionProviderProjectIdMap = useRef(new Map<number, number>()).current;
  const isProspectTable = useRef(props.providerType === ProviderNetworkTypeEnum.Prospect).current;
  const cargoColumns = useRef<Set<string>>(new Set<string>()).current;
  const nextClosestProviderService = useRef(new NextClosestProviderService()).current;
  const take = 50;
  const userContext = useContext(UserContext);
  const flyoutContext = useContext(FlyoutContext);
  const [servicingProviders, setServicingProviders] = useState<ServicingProvider[]>([]);
  const [excludeToolTip, setExcludeToolTip] = useState<string>("");
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedVolumeLabels, setSelectedVolumeLabels] = useState<ClaimsActivityVolumeLabelEnum[]>(undefined);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(undefined);
  const [selectedLocationConfidenceValues, setSelectedLocationConfidenceValues] = useState<LocationConfidenceScoresLabelEnum[]>(undefined);
  const [selectedMotiveAppropriatenessValues, setSelectedMotiveAppropriatenessValues] = useState<MotiveScoreValueEnum[]>(undefined);
  const [selectedNewPatientClaimsValues, setSelectedNewPatientClaimsValues] = useState<NewPatientClaimsEnum[]>(undefined);
  const [selectedCareJourneyCostValues, setSelectedCareJourneyCostValues] = useState<CareJourneyScoresValueEnum[]>(undefined);
  const [selectedCareJourneyQualityValues, setSelectedCareJourneyQualityValues] = useState<CareJourneyScoresValueEnum[]>(undefined);
  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
  const [selectedProjectionProvider, setSelectedProjectionProvider] = useState<ProjectionProvider>(undefined);
  const [selectedProjectionProviderType, setSelectedProjectionProviderType] = useState<ProjectionProviderType>(undefined);
  const [selectedCanEdit, setSelectedCanEdit] = useState<boolean>(false);
  const [selectedFlyoutButtonRef, setSelectedFlyoutButtonRef] = useState<number>(-1);
  const flyoutButtonRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  const [selectedProcedureValues, setSelectedProcedureValues] = useState<string[]>(undefined);
  const [topProcedures, setTopProcedures] = useState<DropdownItem<TopProcedure>[]>([]);
  const [selectedConditionValues, setSelectedConditionValues] = useState<string[]>([]);
  const [selectedSpecialtyPredictionModelFilter, setSelectedSpecialtyPredictionModelFilter] = useState<SpecialtyPredictionModelEnum>(undefined);
  const [selectedPgrConfidenceTiers, setSelectedPgrConfidenceTiers] = useState<number[]>([]);
  const [topConditions, setTopConditions] = useState<DropdownItem<TopCondition>[]>([]);

  const providerPlurality = useMemo(() => {
    return selectedProviders.length === 1 ? "provider" : "providers";
  }, [selectedProviders]);

  const selectedServicingProviders = useMemo(() => {
    return servicingProviders.filter((provider) => selectedProviders.find((p) => provider.providerId.toString() === p));
  }, [servicingProviders, selectedProviders]);

  useEffect(() => {
    // prevent it from calling before the accordion opens
    if (providerData) {
      fetchProviders();
    }
  }, [props.viewableFields, displayedProviderType]);

  useEffect(() => {
    if ((props.shouldRefreshMarketProviders && !isProspectTable) ||
      (props.shouldRefreshProspectProviders && isProspectTable)) {
      fetchProviders();
    }
  }, [props.shouldRefreshMarketProviders, props.shouldRefreshProspectProviders]);

  const fetchProviders = (
    newSkip?: number,
    newSelectedColumn?: keyof NextClosestImpactProviderTableDataWithCargo,
    newSortDirection?: SortDirections,
    cargoSubKey?: string,
    ncpFilterValue?: NcpEnum,
    newDisplayedProviderType?: DisplayedProviderType,
    newSearchValue?: string,
    newEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    newEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    newVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    newLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    newLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    newMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    newNewPatientClaimsValues?: NewPatientClaimsEnum[],
    newSelectedTopProcedures?: string[],
    newCareJourneyCostScoreValues?: CareJourneyScoresValueEnum[],
    newCareJourneyQualityScoreValues?: CareJourneyScoresValueEnum[],
    newSelectedTopConditions?: string[],
    newSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    newPgrConfidenceTiers?: number[],
  ) => {
    setLoading(true);

    if (props.project.showPciData &&
      newLineOfBusiness === undefined &&
      newVolumeLabels === undefined &&
      newEffectivenessValues === undefined &&
      newEfficiencyValues === undefined &&
      newLocationConfidenceValues === undefined &&
      newMotiveAppropriatenessValues === undefined &&
      newNewPatientClaimsValues === undefined &&
      newSelectedTopProcedures === undefined &&
      newCareJourneyCostScoreValues === undefined &&
      newCareJourneyQualityScoreValues === undefined &&
      newSelectedTopConditions === undefined
    ) {
      // pci-specific defaults for on load
      newEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
      newEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
      newLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
      newVolumeLabels = [...Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => x)];
      newLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
      newMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
      newNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
      newSelectedTopProcedures = [];
      newCareJourneyCostScoreValues = Object.values(CareJourneyScoresValueEnum);
      newCareJourneyQualityScoreValues = Object.values(CareJourneyScoresValueEnum);
      newSelectedTopConditions = [];
    }

    const showingExcluded = newDisplayedProviderType != null ? newDisplayedProviderType === "excluded" : displayedProviderType === "excluded";
    const requestProviders = (excludedProviders: boolean, requestSkip: number, requestTake: number) => nextClosestProviderService.getNextClosestImpactProviders(
      props.project?.projectId,
      props.regionSetId,
      props.runConfiguration?.projectRunConfigurationId,
      props.specialtyGroupCode,
      props.networkAssessmentAnalysisType,
      props.providerType,
      requestSkip,
      requestTake,
      newSelectedColumn as string || selectedColumn,
      newSortDirection || sortDirection,
      cargoSubKey || selectedCargoColumn,
      ncpFilterValue || ncpFilter,
      excludedProviders,
      newSearchValue !== undefined ? newSearchValue : searchValue, // if newSearchValue is "" we want to apply that
      newEffectivenessValues !== undefined ? newEffectivenessValues : selectedEffectivenessValues,
      newEfficiencyValues !== undefined ? newEfficiencyValues : selectedEfficiencyValues,
      newVolumeLabels !== undefined ? newVolumeLabels : selectedVolumeLabels,
      newLineOfBusiness !== undefined ? newLineOfBusiness : selectedLineOfBusiness,
      newLocationConfidenceValues !== undefined ? newLocationConfidenceValues : selectedLocationConfidenceValues,
      newMotiveAppropriatenessValues !== undefined ? newMotiveAppropriatenessValues : selectedMotiveAppropriatenessValues,
      newNewPatientClaimsValues !== undefined ? newNewPatientClaimsValues : selectedNewPatientClaimsValues,
      newSelectedTopProcedures !== undefined ? newSelectedTopProcedures : selectedProcedureValues,
      newCareJourneyCostScoreValues !== undefined ? newCareJourneyCostScoreValues : selectedCareJourneyCostValues,
      newCareJourneyQualityScoreValues !== undefined ? newCareJourneyQualityScoreValues : selectedCareJourneyQualityValues,
      newSelectedTopConditions !== undefined ? newSelectedTopConditions : selectedConditionValues,
      newSpecialtyPredictionModelFilter !== undefined ? newSpecialtyPredictionModelFilter : selectedSpecialtyPredictionModelFilter,
      newPgrConfidenceTiers !== undefined ? newPgrConfidenceTiers : selectedPgrConfidenceTiers
    );

    // skip can be 0 and that's okay
    requestProviders(showingExcluded, newSkip === undefined ? skip : newSkip, take).then((newProviderData) => {
      if (isProspectTable || displayedProviderType === "excluded") {
        newProviderData.providers.forEach((p) => {
          if (isProspectTable) {
            p.cargoData.forEach((cd) => {
              cargoColumns.add(cd.name);
            })
          }
          if (displayedProviderType === "excluded") {
            providerIdToProjectionProviderProjectIdMap.set(p.providerId, (p as ProjectionProvider).projectionProviderProjectId);
          }
        });
        if (cargoSubKey) setSelectedCargoColumn(cargoSubKey);
      }

      if (displayedProviderType === "excluded" && newProviderData.providers.length === 0 && !(newSearchValue || searchValue)) {
        setDisplayedProviderType("nonExcluded");
      }

      if (!isEqual(newProviderData?.topConditions.map(x => x.code), topConditions.map(x => x.key.code))) {
        const mappedTopConditions: DropdownItem<TopCondition>[] = newProviderData?.topConditions.map(x => {
          return { key: x, name: `${x.code} - ${x.description}` }
        });
        setTopConditions(mappedTopConditions);
      }

      if (!isEqual(newProviderData?.topProcedures.map(x => x.code), topProcedures.map(x => x.key.code))) {
        const mappedTopProcedures: DropdownItem<TopProcedure>[] = newProviderData?.topProcedures.map(x => {
          return { key: x, name: `${x.code} - ${x.description}` }
        });
        setTopProcedures(mappedTopProcedures);
      }

      setServicingProviders(newProviderData.providers);
      // The response reports excluded providers unfiltered, so the toggle follows the filtered excluded list.
      if (showingExcluded) {
        setHasExcludedRecords(newProviderData.totalCount > 0);
      } else if (newProviderData.hasExcludedNextClosest) {
        requestProviders(true, 0, 1).then((excludedData) => setHasExcludedRecords(excludedData.totalCount > 0));
      } else {
        setHasExcludedRecords(false);
      }
      setAccessStandard(newProviderData.accessStandardText);
      setProviderData(convertProviderToTableData(newProviderData.providers as NextClosestImpactProvider[]));
      setTotalRecords(newProviderData.totalCount);
      setShowTime(newProviderData.showTime);
      setShowDist(newProviderData.showDistance)
      if (newSkip === 0 || !!newSkip) setSkip(newSkip);
      if (newSelectedColumn) setSelectedColumn(newSelectedColumn as string);
      if (newSortDirection) setSortDirection(newSortDirection);
      if (ncpFilterValue) setNcpFilter(ncpFilterValue);
      if (newSearchValue !== undefined) setSearchValue(newSearchValue);
      if (newEffectivenessValues !== undefined) setSelectedEffectivenessValues(newEffectivenessValues);
      if (newEfficiencyValues !== undefined) setSelectedEfficiencyValues(newEfficiencyValues);
      if (newVolumeLabels !== undefined) setSelectedVolumeLabels(newVolumeLabels);
      if (newLineOfBusiness !== undefined) setSelectedLineOfBusiness(newLineOfBusiness);
      if (newLocationConfidenceValues !== undefined) setSelectedLocationConfidenceValues(newLocationConfidenceValues);
      if (newMotiveAppropriatenessValues !== undefined) setSelectedMotiveAppropriatenessValues(newMotiveAppropriatenessValues);
      if (newNewPatientClaimsValues !== undefined) setSelectedNewPatientClaimsValues(newNewPatientClaimsValues);
      if (newSelectedTopProcedures !== undefined) setSelectedProcedureValues(newSelectedTopProcedures);
      if (newCareJourneyCostScoreValues !== undefined) setSelectedCareJourneyCostValues(newCareJourneyCostScoreValues);
      if (newCareJourneyQualityScoreValues !== undefined) setSelectedCareJourneyQualityValues(newCareJourneyQualityScoreValues);
      if (newSelectedTopConditions !== undefined) setSelectedConditionValues(newSelectedTopConditions);
      if (newSpecialtyPredictionModelFilter !== undefined) setSelectedSpecialtyPredictionModelFilter(newSpecialtyPredictionModelFilter);
      if (newPgrConfidenceTiers !== undefined) setSelectedPgrConfidenceTiers(newPgrConfidenceTiers);
      setExcludeToolTip((userContext.roles.includes(Roles.TIN_VIEWER) && (servicingProviders.every((provider) => !provider.npi && provider.tin) && servicingProviders.every((provider) => provider.npi && !provider.tin))) ? "All selected providers must have NPIs or all selected providers must have TINs." : "");
      const newFlyoutRefs = [];
      for (const provider of newProviderData.providers) {
        newFlyoutRefs[provider.providerId] = React.createRef<HTMLButtonElement>();
      }
      flyoutButtonRefs.current = newFlyoutRefs;
      setLoading(false);
    });
  };

  const isFieldVisible = (fieldName: NPF) => {
    return props.viewableFields?.find((vf) => vf.backingFieldName === fieldName)?.visible;
  }

  const getProviderProfile = (provider: NextClosestImpactProvider) => {
    return (
      <ServicingProviderProfile
        {...props}
        isTerminated={false}
        nextClosestProvider={true}
        provider={provider}
        providerNetworkType={props.providerType}
      />
    )
  }

  const convertProviderToTableData = (providers: NextClosestImpactProvider[]) => {
    return providers.map<NextClosestImpactProviderTableDataWithCargo>((p) => {
      const { specialty, address, ...commonColumns } = buildCommonImpactProviderColumns(p, props.viewableFields, props.runConfiguration);
      const providerCargoData: Partial<NextClosestImpactProviderTableDataWithCargo> = isProspectTable ? getCargoDataForProvider(p, props.viewableFields, cargoColumns) : {};
      const checkIcon = <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top" />;
      const xCloseIcon = <QesIcon iconName={QesIcons.XClose} isDecorative className="align-text-top" />;

      /* oxlint-disable sort-keys */
      const td: NextClosestImpactProviderTableDataWithCargo = {
        [wafflesKeyColumnName]: p.providerId.toString(),
        displayName: (
          <span>
            <FlyoutLink
              classNames="p-0 qes-button-secondary"
              content={{ body: getProviderProfile(p), title: ACCESS_POINT_PROFILE_FLYOUT_TITLE }}
              testId="next-closest-impact-providers-panel__provider-profile-flyout"
            >
              {p.displayName}
            </FlyoutLink>
            <br />
            {isFieldVisible(NPF.NPI) && p.npi && <span className="text-muted ml15 d-block">NPI: {p.npi}</span>}
            {isFieldVisible(NPF.TIN) && p.tin && (
              <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                <span className="text-muted ml15 d-block">TIN: {p.tin}</span>
              </RoleRestricted>
            )}
            {isFieldVisible(NPF.OrganizationName) && p.organizationName && p.entityType === EntityTypeEnum.Individual && <span className="text-muted ml15 d-block">{p.organizationName}</span>}
          </span>
        ),
        averageDistance: p.averageDistance,
        averageTime: p.averageTime,
        memberCount: p.memberCount,
        npi: p.npi,
        totalAverageDistance: p.totalAverageDistance,
        totalAverageTime: p.totalAverageTime,
        totalMemberCount: p.totalMemberCount,
        specialty,
        address,
        ...commonColumns as ImpactProviderTableData,
        projectionReason: (() => {
          const projectionProvider = p as unknown as ProjectionProvider;
          return (
            <>
              {projectionProvider.processed === false && <div className="demibold">UNPROCESSED</div>}
              {displayedProviderType === "excluded" && (
                <>
                  {(userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER) && props.runConfiguration.isLatestPublished) && (
                    <TooltipWrapper text="Edit/View Excluded Provider Details">
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={() => handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.ExcludeImpact, true)}
                        qesTestId={`next-closest-impact-providers-panel__exclude--edit--${projectionProvider.providerId}`}
                        qesType="none"
                        qesRef={flyoutButtonRefs.current[projectionProvider.providerId]}
                      >
                        {projectionProvider.reasonName ?
                          <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                  {(!userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER) || props.runConfiguration.datePublished == null) && (
                    <TooltipWrapper text="View Excluded Provider Details">
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={() => handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.ExcludeImpact, false)}
                        qesTestId={`next-closest-impact-providers-panel__exclude--no-edit--${projectionProvider.providerId}`}
                        qesType="none"
                        qesRef={flyoutButtonRefs.current[projectionProvider.providerId]}
                      >
                        {projectionProvider.reasonName ?
                          <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                </>
              )
              }
              {(projectionProvider.allLocations || projectionProvider.allSpecialties || projectionProvider.allProjects) &&
                <>
                  {projectionProvider.allLocations && projectionProvider.allSpecialties && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon} All Locations & Specialties</div></>}
                  {projectionProvider.allLocations && !projectionProvider.allSpecialties && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon} All Locations</div></>}
                  {projectionProvider.allSpecialties && !projectionProvider.allLocations && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon} All Specialties</div></>}
                  {displayedProviderType === "excluded" && projectionProvider.allProjects && <><div>{xCloseIcon} All Projects</div></>}
                </>
              }
            </>
          );
        })(),
        projectionReasonDescription: <div className="impact-providers__projection-reason">{ellipsifyDescription((p as unknown as ProjectionProvider).reasonDescription)}</div>,
        ...providerCargoData, // will be {} for ProviderNetworkType.Market tables
        locationConfidenceScoresDisplay: p.locationConfidenceScoresDisplay ? p.locationConfidenceScoresDisplay : "---",
      };
      /* oxlint-enable sort-keys */

      return td;
    });
  }

  const ellipsifyDescription = (description: string) => {
    if (!description || description.length < 250) {
      return description;
    }
    return (
      <TooltipWrapper text={description} tooltipWidth="350px">
        {description.substring(0, 250)}...
      </TooltipWrapper>
    );
  }

  const handleOpenEditProviderFlyout = (projectionProvider: ProjectionProvider, projectionProviderType: ProjectionProviderType, canEdit: boolean) => {
    setSelectedCanEdit(canEdit);
    setSelectedFlyoutButtonRef(projectionProvider.providerId);
    setSelectedProjectionProvider(projectionProvider);
    setSelectedProjectionProviderType(projectionProviderType);
    flyoutContext.setIsFlyoutVisible(true);
  };

  const accordionTitle = useMemo(() => {
    const tableType = props.providerType.charAt(0).toUpperCase() + props.providerType.substring(1);
    const aliasString = props.providerType === ProviderNetworkTypeEnum.Market
      ? !!props.runConfiguration.marketProviderSetAlias
        ? ` (${props.runConfiguration.marketProviderSetAlias})`
        : ""
      : props.providerType === ProviderNetworkTypeEnum.Prospect
        ? !!props.runConfiguration.prospectProviderSetAlias
          ? ` (${props.runConfiguration.prospectProviderSetAlias})`
          : ""
        : "";
    let base = `${displayedProviderType == "excluded" ? "Excluded " : ""}Next Closest ${tableType} Providers${aliasString}`;
    if (isOpen && !!providerData) {
      base += ` (${Math.min(providerData.length, totalRecords)} of ${totalRecords}${searchValue ? `, filtered to ${searchValue}` : ""})`;
    }
    return base;
  }, [providerData, isOpen, totalRecords]);

  //const allowExclude = props.runConfiguration.isLatestPublished || props.runConfiguration.isTermed || props.runConfiguration.isProjected || props.runConfiguration.isProjectedTermed;

  const excludedToggle = useMemo(() => {
    return (
      <div className="clearfix">
        <div className="pull-right">
          <Toggle
            label="Excluded"
            active={displayedProviderType === "excluded"}
            onChange={() => {
              setDisplayedProviderType(displayedProviderType === "excluded" ? "nonExcluded" : "excluded");
              setSelectedProviders([]);
              setSearchValue("");
              setSelectedEffectivenessValues(undefined);
              setSelectedEfficiencyValues(undefined);
              setSelectedVolumeLabels(undefined);
              setSelectedLineOfBusiness(undefined);
              setSelectedLocationConfidenceValues(undefined);
              setSelectedMotiveAppropriatenessValues(undefined);
              setSelectedNewPatientClaimsValues(undefined);
              setSelectedProcedureValues([]);
              setSelectedCareJourneyCostValues(undefined);
              setSelectedCareJourneyQualityValues(undefined);
              setDisplayedProviderTypeChanged(true);
              setSelectedConditionValues([]);
            }}
            containerClassName="me-3"
            testId="next-closest-excluded-toggle"
          />
        </div>
      </div>
    );
  }, [displayedProviderType]);

  // we had this useMemo'd in the past, but it caused problems with the state values called in fetchProviders
  // given that NcpEnum only has three entries, iterating over it frequently isn't a big deal
  const ncpDropdown = (
    <div className="clearfix">
      <div className="pull-right mb-2">
        <NewActionMenu
          buttonText={`Display: ${translateNcpFilter(ncpFilter)}`}
          buttonClassNames="qes-button-secondary"
          testId="next-closest-impact-providers-panel__display-dropdown"
          key={ncpFilter}
        >
          {Object.values(NcpEnum).map((value: NcpEnum) => {
            const name = translateNcpFilter(value);
            const isSelected = value === ncpFilter;
            if (name) {
              return (
                <NewActionMenuItem
                  key={value}
                  display={name}
                  isSelected={isSelected}
                  onClick={() => fetchProviders(0, undefined, undefined, undefined, value)}
                  testId={`next-closest-impact-providers-panel__display-option--${value}`}
                />
              )
            }
            return null;
          })}
        </NewActionMenu>
      </div>
    </div>
  );

  const handleProjectionProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      props.processProviderChanges(false);
    } else {
      props.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
      setSelectedProviders([]);
      fetchProviders(0, undefined, undefined, undefined, NcpEnum.All, undefined);
      if (isProspectTable) {
        props.setShouldRefreshProspectProviders(true);
      } else {
        props.setShouldRefreshMarketProviders(true);
      }
    }
  }

  const handleProjectionProvidersDeleted = async (runProjection: boolean) => {
    await handleProjectionProviderCreatedOrUpdated(runProjection, true);
  }

  const bulkActionBar = useMemo(() => {
    if ((props.runConfiguration.isLatestPublished || props.runConfiguration.isTermed || props.runConfiguration.isProjected || props.runConfiguration.isProjectedTermed) &&
      !props.project.isEditRestricted) {
      if (displayedProviderType !== "excluded") {
        return (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
            <span className={`bulk-action-group${(selectedProviders.length > 0 && excludeToolTip !== "") ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: (
                    <ProjectionProviderDetails
                      accessPoints={convertServicingProvidersToAccessPoints(selectedServicingProviders, props.providerType, props.specialtyGroupCode)}
                      showAffiliationsInsights={props.project.showAffiliationsInsights}
                      formState={projectionProvidersState}
                      formDispatch={projectionProvidersReducer}
                      hasProvidersWithoutNPI={providerData && (providerData.filter((provider) => (selectedProviders.includes(provider.key))).find((provider) => provider.npi === undefined) != undefined)}
                      networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
                      projectId={props.project.projectId}
                      projectionPanelMode={ProjectionPanelMode.Create}
                      projectionProviderType={ProjectionProviderType.ExcludeImpact}
                      providerNetworkType={props.providerType}
                      onProjectionProvidersCreated={handleProjectionProviderCreatedOrUpdated}
                      specialtyGroupCode={props.specialtyGroupCode}
                      isZipProject={false}
                      isRunning={props.project?.isRunning}
                    />
                  ),
                  title: `Exclude ${capitalizeFirstLetter(providerPlurality)}`
                }}
                disabled={selectedProviders.length === 0}
                testId="next-closest-impact-providers-panel__exclude"
              >
                <TooltipWrapper text={excludeToolTip === "" ? `Exclude selected ${providerPlurality}` : excludeToolTip}>
                  <QesIcon iconName={QesIcons.SlashOctagon} description="Exclude" className="align-text-top me-1" /> Exclude
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          </RoleRestricted >
        );
      } else {
        return (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
            <span className={`bulk-action-group${selectedProviders.length > 0 ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: (
                    <DeleteProjectionProvider
                      netNewServicingProviderProjectIds={[]}
                      projectId={props.project.projectId}
                      projectionProviderProjectIds={selectedProviders.map((p) => providerIdToProjectionProviderProjectIdMap.get(parseInt(p, 10)))}
                      projectionProviderType={ProjectionProviderType.ExcludeImpact}
                      providerNetworkType={props.providerType}
                      onProjectionProvidersDeleted={handleProjectionProvidersDeleted}
                      wrapperComponentType="flyoutLink"
                    />
                  ),
                  title: `Delete Excluded ${capitalizeFirstLetter(providerPlurality)}`,
                }}
                disabled={selectedProviders.length === 0}
                testId="next-closest-impact-providers-panel__remove"
              >
                <TooltipWrapper text={`Stop excluding selected ${providerPlurality}`}>
                  <QesIcon iconName={QesIcons.TrashV1} description="Remove" className="align-text-top me-1" /> Remove
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          </RoleRestricted>
        );
      }
    }

    return null;
  }, [selectedProviders, props.runConfiguration, props.project, displayedProviderType, projectionProvidersState]);

  const handleProviderSearch = (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedProcedureValues?: string[],
    selectedConditionValues?: string[],
    selectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[]
  ) => {
    fetchProviders(
      0, // a new filter starts from the first page
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      searchValue,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues,
      selectedProcedureValues,
      selectedCareJourneyCostValues,
      selectedCareJourneyQualityValues,
      selectedConditionValues,
      selectedSpecialtyPredictionModelFilter,
      selectedPgrConfidenceTiers
    );
  }

  const handleConfidenceTierSearch = (searchValue: string, selectedPgrConfidenceTiers: number[]) => {
    handleProviderSearch(
      searchValue,
      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
      undefined, undefined, undefined, undefined, undefined, undefined,
      selectedPgrConfidenceTiers
    );
  }

  const accordionTestId = useMemo(() => {
    return props.providerType === ProviderNetworkTypeEnum.Market ? "next-closest-market-providers-panel" :
      props.providerType === ProviderNetworkTypeEnum.Prospect ? "next-closest-prospect-providers-panel" :
        "";
  }, [props.providerType]);

  const updateChangeTracker = () => {
    if (displayedProviderTypeChanged) {
      setDisplayedProviderTypeChanged(false);
    }
  }

  const providerSearchDescriptiveText = `This will search each provider's name, NPI,${userContext.roles.includes(Roles.TIN_VIEWER) ? " TIN," : ""} specialty, and address.`;

  const renderSearchField = (): JSX.Element => {
    let tableType: TableType;
    switch (props.providerType) {
      case ProviderNetworkTypeEnum.Market:
        tableType = TableType.NextClosestMarketProviders;
        break;
      case ProviderNetworkTypeEnum.Prospect:
        tableType = TableType.NextClosestProspectProviders;
        break;
      default:
        break;
    }

    if (props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          clearFilters={displayedProviderTypeChanged}
          handleProviderSearch={handleProviderSearch}
          lastSearchValue={searchValue}
          lastSelectedEffectivenessValues={selectedEffectivenessValues}
          lastSelectedEfficiencyValues={selectedEfficiencyValues}
          lastSelectedGainsPercent={null}
          lastSelectedLineOfBusiness={selectedLineOfBusiness}
          lastSelectedVolumeLabels={selectedVolumeLabels}
          lastSelectedLocationConfidenceValues={selectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={selectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={selectedNewPatientClaimsValues}
          lastSelectedProcedureValues={selectedProcedureValues}
          topProcedures={topProcedures}
          lastSelectedConditionValues={selectedConditionValues}
          topConditions={topConditions}
          lastSelectedCareJourneyCostValues={selectedCareJourneyCostValues}
          lastSelectedCareJourneyQualityValues={selectedCareJourneyQualityValues}
          tableType={tableType}
          validLoadStatus={!loading}
          showLocationConfidence={props.runConfiguration.showLocationConfidenceScores && props.project.showLocationConfidenceScores}
          updateChangeTracker={updateChangeTracker}
          showCareJourneyScores={props.runConfiguration.showCareJourneyScores}
          showSpecialtyPredictionModel={props.showSpecialtyPredictionModel}
          lastSelectedSpecialtyPredictionModelFilter={selectedSpecialtyPredictionModelFilter}
          lastSelectedConfidenceTierValues={selectedPgrConfidenceTiers}
          showConfidenceTier={props.showPgrConfidenceTier}
        />
      );
    } else if (props.showPgrConfidenceTier) {
      return (
        <ConfidenceTierSearchFilters
          clearSearch={displayedProviderTypeChanged}
          descriptiveText={providerSearchDescriptiveText}
          handleProviderSearch={handleConfidenceTierSearch}
          lastSearchValue={searchValue}
          lastSelectedConfidenceTierValues={selectedPgrConfidenceTiers}
          tableType={tableType}
          updateChangeTracker={updateChangeTracker}
          validLoadStatus={!loading}
        />
      );
    } else {
      return (
        <SearchBox
          /* forcefully clears the search box on the dislplayed provider type changes */
          key={displayedProviderType}
          descriptiveText={providerSearchDescriptiveText}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`${props.providerType}-provider-search`}
          onSubmit={(value: string) => { handleProviderSearch(value) }}
        />
      );
    }
  }

  return (
    <>
      <AccordionContainer>
        <Accordion
          defaultOpen={false}
          title={accordionTitle}
          onPanelWillOpen={!providerData && fetchProviders}
          onPanelDidOpen={() => setIsOpen(true)}
          onPanelDidClose={() => setIsOpen(false)}
          testId={accordionTestId}
        >
          {
            providerData && props.viewableFields ?
              props.editRestricted || props.readOnly ?
                <p className="copy-text-default mb-0">Next Closest Provider Results are currently being calculated.</p> :
                <>
                  {renderSearchField()}
                  {providerData.length > 0 || ncpFilter !== NcpEnum.All ?
                    (
                      <>
                        <p className="copy-text-default">The following are based on members without access. These providers are outside access standard of {accessStandard}.</p>
                        {ncpDropdown}
                        {hasExcludedRecords && excludedToggle}
                        <NextClosestImpactProviderTable
                          bulkActionBar={bulkActionBar}
                          data={providerData}
                          displayedProviderType={displayedProviderType}
                          providerType={props.providerType}
                          viewableFields={props.viewableFields}
                          handleSort={(col, direction) => { fetchProviders(0, col, direction) }}
                          loading={loading}
                          onSelectedProvidersChanged={setSelectedProviders}
                          project={props.project}
                          runConfiguration={props.runConfiguration}
                          selectedProviders={selectedProviders}
                          setSkip={(newSkip: number) => fetchProviders(newSkip)}
                          showDist={showDist}
                          showTime={showTime}
                          skip={skip}
                          tableType={isProspectTable ? TableType.NextClosestProspectProviders : TableType.NextClosestMarketProviders}
                          take={take}
                          totalRecords={totalRecords}
                          showProviderValidations={props.showProviderValidations}
                          showSpecialtyPredictionModel={props.showSpecialtyPredictionModel}
                          showPgrConfidenceTier={props.showPgrConfidenceTier}
                        />
                      </>
                    ) :
                    (<>
                        {hasExcludedRecords && excludedToggle}
                        <p className="copy-text-default mb-0">There are no next closest {props.providerType} provider results.</p>
                      </>
                    )}
                </> :
              <LoadingSpinner />
          }
        </Accordion>
      </AccordionContainer>
      <Flyout
        triggerElement={flyoutButtonRefs.current[selectedFlyoutButtonRef]}
        content={{
          body: <ProjectionProviderDetails
            {...props}
            accessPoints={convertServicingProvidersToAccessPoints(selectedServicingProviders, props.providerType, props.specialtyGroupCode)}
            hasProvidersWithoutNPI={selectedProjectionProvider?.npi != undefined}
            formState={projectionProvidersState}
            formDispatch={projectionProvidersReducer}
            networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
            projectId={props.project.projectId}
            projectionPanelMode={selectedCanEdit ? ProjectionPanelMode.Edit : ProjectionPanelMode.View}
            projectionProvider={selectedProjectionProvider}
            projectionProviderType={selectedProjectionProviderType}
            providerNetworkType={props.providerType}
            onProjectionProviderUpdated={handleProjectionProviderCreatedOrUpdated}
            specialtyGroupCode={props.specialtyGroupCode}
            isZipProject={false}
            showAffiliationsInsights={props.project.showAffiliationsInsights}
            isRunning={props.project?.isRunning}
          />,
          title: "Exclusion Details",
        }}
      />
    </>
  );
}

const WrappedNextClosestImpactProvidersPanel: React.FC<NextClosestImpactProvidersPanelProps> = (props: NextClosestImpactProvidersPanelProps) => {
  return (
    <FlyoutContextProvider>
      <NextClosestImpactProvidersPanel {...props} />
    </FlyoutContextProvider>
  )
};

export { WrappedNextClosestImpactProvidersPanel as NextClosestImpactProvidersPanel };